import {
  CHAT_PAGE_LIMIT,
} from "./session-data-provider-utils";
import type { SessionProviderPlatform } from "./use-session-provider-platform";
import type { SessionProviderStateBundle } from "./use-session-provider-state-bundle";
import type { SessionProviderLiveWiring } from "./use-session-provider-live-wiring";
import type { SessionProviderOverviewWiring } from "./use-session-provider-overview-wiring";
import { useSessionProviderInitialLoadWiring } from "./use-session-provider-initial-load-wiring";
import { useSessionNavigation } from "./use-session-navigation";
import { useSessionPolling } from "./use-session-polling";
import { useSessionPollingRestart } from "./use-session-polling-restart";

export interface SessionProviderRuntimeWiring {
  navigation: ReturnType<typeof useSessionNavigation>;
  polling: ReturnType<typeof useSessionPolling>;
}

interface SessionProviderRuntimeWiringInput {
  platform: SessionProviderPlatform;
  sessionId: string;
  state: SessionProviderStateBundle;
  liveWiring: SessionProviderLiveWiring;
  overviewWiring: SessionProviderOverviewWiring;
}

export function useSessionProviderRuntimeWiring(
  input: SessionProviderRuntimeWiringInput,
): SessionProviderRuntimeWiring {
  const navigation = useSessionProviderNavigationWiring(input);
  const polling = useSessionProviderPollingWiring(input);

  useSessionProviderInitialLoadWiring({
    platform: input.platform,
    sessionId: input.sessionId,
    state: input.state,
    liveWiring: input.liveWiring,
    overviewWiring: input.overviewWiring,
    navigation,
    polling,
  });

  useSessionProviderPollingRestartWiring({ input, polling });

  return {
    navigation,
    polling,
  };
}

function useSessionProviderNavigationWiring(
  input: SessionProviderRuntimeWiringInput,
): ReturnType<typeof useSessionNavigation> {
  return useSessionNavigation({
    api: input.platform.api,
    sessionId: input.sessionId,
    products: input.state.entity.products,
    productsRef: input.state.refs.productsRef,
    rootProductIdRef: input.state.refs.rootProductIdRef,
    urlRootProductId: input.state.meta.urlRootProductId,
    collabsByProduct: input.state.conversation.collabsByProduct,
    chatPageLimit: CHAT_PAGE_LIMIT,
    refClickLog: input.platform.refClickLog,
    setChat: input.state.interaction.setChat,
    replaceProductCollabItems:
      input.overviewWiring.collabMerges.replaceProductCollabItems,
  });
}

function useSessionProviderPollingWiring(
  input: SessionProviderRuntimeWiringInput,
): ReturnType<typeof useSessionPolling> {
  return useSessionPolling({
    api: input.platform.api,
    router: input.platform.router,
    sessionId: input.sessionId,
    sessionGenerationRef: input.state.refs.sessionGenerationRef,
    timestamps: input.liveWiring.timestamps.timestamps,
    haltStatusDebugLog: input.platform.haltStatusDebugLog,
    applySessionFromSource:
      input.overviewWiring.overview.applySessionFromSource,
    canFlashForPoll: input.liveWiring.liveSignals.canFlashForPoll,
    flagMemberChanges: input.liveWiring.liveSignals.flagMemberChanges,
    setMembers: input.state.entity.setMembers,
    mergeProductsAndFlagChanges:
      input.liveWiring.liveSignals.mergeProductsAndFlagChanges,
    updateProductsSeen: input.liveWiring.readTracking.updateProductsSeen,
    mergePolledChatItems:
      input.overviewWiring.collabMerges.mergePolledChatItems,
    mergePolledProductCollabItems:
      input.overviewWiring.collabMerges.mergePolledProductCollabItems,
    mergeProductReadItems: input.liveWiring.readTracking.mergeProductReadItems,
    updateActivitySinceTimestamp:
      input.liveWiring.timestamps.updateActivitySinceTimestamp,
    updateProductsSinceTimestamp:
      input.liveWiring.timestamps.updateProductsSinceTimestamp,
    updateProductCollabsSinceTimestamp:
      input.liveWiring.timestamps.updateProductCollabsSinceTimestamp,
    updateProductReadsSinceTimestamp:
      input.liveWiring.timestamps.updateProductReadsSinceTimestamp,
  });
}

function useSessionProviderPollingRestartWiring({
  input,
  polling,
}: {
  input: SessionProviderRuntimeWiringInput;
  polling: ReturnType<typeof useSessionPolling>;
}): void {
  useSessionPollingRestart({
    sessionStatusValue: input.overviewWiring.overview.session?.status,
    sessionGenerationRef: input.state.refs.sessionGenerationRef,
    haltStatusDebugLog: input.platform.haltStatusDebugLog,
    isPollingRunning: polling.isPollingRunning,
    clearPolling: polling.clearPolling,
    startPolling: polling.startPolling,
  });
}
